import { useEffect, useMemo, useState } from 'react';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { GreenPlateDTO } from 'api/model';
import { makeBooleanComparator } from 'utils/sorting';
import {
  Centered,
  getTableRowClassName,
} from 'components/Dashboard/table-helpers';
import Widget from 'components/Dashboard/Widget';
import { GreenPlatesWidgetsEnum, LongTitles, UPDATE_INTERVAL } from '../utils';
import UserModalLink from 'components/UserModalLink';
import styled from 'styled-components';
import { getHRGetGreenPlatesQueryKey, useHRGetGreenPlates } from 'api/hr/hr';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from 'utils/date-helpers';
import Select from 'components/inputs/Select';
import useGreenPlates from './useGreenPlates';
import LabelWrap from 'components/inputs/LabelWrap';

const TABLE_NAME = 'green-plates-list';

type ActiveSelection = 'all' | 'active' | 'inactive';

const WidgetFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;
`;

const GreenPlatesListWidget = () => {
  const [greenPlates, setGreenPlates] = useState<GreenPlateDTO[]>([]);
  const [selectedActiveFilter, setSelectedActiveFilter] =
    useState<ActiveSelection>('active');
  const {
    selectedDateFilter,
    setSelectedDateFilter,
    dateFilterOptions,
    getFilteredByDateOptions,
    getGreenPlateStatusIcon,
  } = useGreenPlates();

  const {
    dashboardState: { selectedBusinessUnits, searchText },
  } = useDashboardControls();

  const getGreenPlates = useHRGetGreenPlates(
    {
      ActiveUserFilter:
        selectedActiveFilter === 'active'
          ? true
          : selectedActiveFilter === 'inactive'
            ? false
            : undefined, // Returns both
      BusinessUnitIDs: selectedBusinessUnits,
    },
    {
      query: {
        refetchInterval: UPDATE_INTERVAL,
      },
    }
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    if (getGreenPlates.data) {
      setGreenPlates(getGreenPlates.data);
    } else {
      setGreenPlates([] as GreenPlateDTO[]);
    }
  }, [getGreenPlates.data]);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: getHRGetGreenPlatesQueryKey(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActiveFilter]);

  const filteredGreenPlates = useMemo(() => {
    // const filteredByDateOptions = getFilteredByDateOptions(mockGreenPlates);
    const filteredByDateOptions = getFilteredByDateOptions(greenPlates);
    if (!searchText.length) {
      return filteredByDateOptions;
    }
    return filteredByDateOptions
      ? filteredByDateOptions.filter((greenPlate) => {
          return (
            greenPlate.greenPlate
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            greenPlate.userFullName
              .toLowerCase()
              .includes(searchText.toLowerCase())
          );
        })
      : [];
  }, [getFilteredByDateOptions, greenPlates, searchText]);

  const columnSettings: ColumnSetting<GreenPlateDTO, any>[] = useMemo(() => {
    const columns: ColumnSetting<GreenPlateDTO, any>[] = [
      {
        head: 'Nr',
        render: (e, t, f, i) => i + 1,
        width: 15,
      },
      {
        head: 'Grönskylt',
        render: (row) => `${row.greenPlate ?? 'Saknar grönskylt'}`,
        width: 50,
        sortFunction: (a, b) => {
          return (a.greenPlate ?? '').localeCompare(b.greenPlate ?? '');
        },
      },
      {
        head: 'Aktiv',
        render: (row) => <span>{row.isActiveUser ? 'Ja' : 'Nej'}</span>,
        width: 50,
        sortFunction: makeBooleanComparator((row) => row.isActiveUser ?? false),
      },
      {
        head: 'Påbörjade uppdrag senast',
        render: (row) => {
          return row.lastUsedByUserAt ? (
            <>
              {getGreenPlateStatusIcon(row.lastUsedByUserAt)}
              &nbsp;
              {formatDateTime(row.lastUsedByUserAt)}
            </>
          ) : (
            ' '
          );
        },
        width: 100,
        sortFunction: (a, b) => {
          const dateA = a.lastUsedByUserAt
            ? new Date(a.lastUsedByUserAt).getTime()
            : 0;
          const dateB = b.lastUsedByUserAt
            ? new Date(b.lastUsedByUserAt).getTime()
            : 0;
          return dateA - dateB;
        },
      },

      {
        head: 'Användare',
        render: (row) => {
          return <span>{row.userFullName ?? ''}</span>;
        },
        width: 100,
        sortFunction: (a, b) => a.userFullName.localeCompare(b.userFullName),
      },
      {
        head: 'Telefonnummer',
        render: (row) => {
          return row.phoneNumber ?? '';
        },
        width: 100,
        sortFunction: (a, b) =>
          (a.phoneNumber ?? '').localeCompare(b.phoneNumber ?? ''),
      },

      {
        head: ' ',
        render: (row) => (
          <Centered>
            <UserModalLink userId={row.userID} />
          </Centered>
        ),
        width: 20,
      },
    ];
    return columns;
  }, [getGreenPlateStatusIcon]);

  return (
    <Widget
      title={LongTitles[GreenPlatesWidgetsEnum.GreenPlatesList]}
      count={Array.isArray(greenPlates) ? greenPlates.length : 0}
      isLoading={getGreenPlates.isLoading}
      info="Lista på alla grönskyltar"
    >
      <WidgetFilters>
        <LabelWrap label="Personalkort:">
          <Select
            value={selectedActiveFilter}
            onChange={(e) => {
              const selectedValue = e.currentTarget.value;
              setSelectedActiveFilter(selectedValue as ActiveSelection);
            }}
          >
            <option value="all">Alla</option>
            <option value="active">Aktiva</option>
            <option value="inactive">Inaktiva</option>
          </Select>
        </LabelWrap>
        <LabelWrap label="Påbörjade uppdrag:">
          <Select
            value={selectedDateFilter}
            onChange={(e) => {
              const selectedValue = e.currentTarget.value;
              setSelectedDateFilter(selectedValue);
            }}
          >
            <option value="">Alla</option>
            <option disabled value="">
              Inte aktiva sedan:
            </option>
            {dateFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </LabelWrap>
      </WidgetFilters>
      <Table
        columnSettings={columnSettings}
        rows={filteredGreenPlates}
        rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.userID)}
        useColumnWidthAsFlexAmount
      />
    </Widget>
  );
};

export default GreenPlatesListWidget;
